<template>
  <UCard
    :ui="publicCard"
    class="fixed left-0 right-0 bottom-0 z-50 lg:z-0 lg:mt-8 lg:relative lg:left-auto lg:right-auto lg:bottom-auto"
  >
    <UForm :schema="schema" :state="state" class="grid grid-cols-3 gap-3">
      <TDateInput
        v-model:dates="state.dates"
        :item-id="props.item.id"
        :message="calendarUpdate"
        :show-icon-mobile="false"
      />
      <UInput
        v-model="state.customers"
        data-test="customers-input"
        type="number"
        min="0"
        class="grow col-span-1 lg:col-span-3"
        :placeholder="$t('views.cottage.aside.customers')"
        :aria-label="$t('views.cottage.aside.customers')"
        size="xl"
      />
      <div class="col-span-3 flex gap-x-2">
        <UButton
          color="gray"
          variant="solid"
          icon="i-heroicons-envelope"
          :aria-label="$t('views.cottage.aside.sendMessage')"
          size="xl"
          :to="goToCottageContact"
          rel="nofollow"
          external
        />
        <UButton
          data-test="submit-button"
          type="submit"
          class="grow flex justify-center"
          size="xl"
          :loading="isLoading"
          @click.prevent="onSubmit"
        >
          {{ $t('views.cottage.aside.toPrices') }}
        </UButton>
      </div>
    </UForm>
  </UCard>
</template>

<script setup lang="ts">
import { z } from 'zod'
import type { DatePickerRangeObject } from 'v-calendar/dist/types/src/use/datePicker.js'
import type { CottagePage } from '../entities/CottagePage'
import { publicCard } from '~/modules/design-system/utils/nuxt-ui-config/components/card'
import { formatCottageCalendarUpdate } from '../utils/formatCottageCalendarUpdate'
import { useSavedDatesRuralityCookie } from '../composables/useSavedDatesRuralityCookie'
import type { SavedDatesRuralityCookie } from '../entities/SavedDatesRuralityCookie'

const { t } = useI18n()
const route = useRoute()
const { getSavedDates, setSavedDates } = useSavedDatesRuralityCookie()

type ItemType = Pick<
  CottagePage,
  'name' | 'province' | 'slug' | 'id' | 'calendarUpdate'
>

interface Props {
  item: ItemType
}

const props = defineProps<Props>()

const schema = z.object({
  dates: z.object({}).nullable(),
  customers: z.number().int(),
})

type SchemaType = z.infer<typeof schema>

interface State {
  dates: DatePickerRangeObject | null
  customers: SchemaType['customers'] | undefined
}

const state = reactive<State>({
  dates: null,
  customers: undefined,
})

const isLoading = ref(false)

const calendarUpdate = computed(() =>
  props.item?.calendarUpdate
    ? formatCottageCalendarUpdate(props.item.calendarUpdate.daysSinceLastUpdate)
    : undefined,
)

const setCookieDates = () => {
  if (!state.dates) return
  const data: SavedDatesRuralityCookie = {
    dateIn: toRuralityDate().formatDate(state.dates.start as Date),
    dateOut: toRuralityDate().formatDate(state.dates.end as Date),
  }
  if (state.customers) {
    data.capacity = state.customers.toString()
  }
  setSavedDates(data)
}

const goToCottageContact = routes().cottageContact({
  slug: route.params.slug as string,
  province: route.params.province as string,
})

const onSubmit = () => {
  if (!import.meta.client) return

  if (schema.safeParse(state).error || state.dates === null) {
    alert(t('views.cottage.aside.noData'))
  } else {
    isLoading.value = true

    setCookieDates()
    navigateTo(
      routes().prices({
        slug: props.item.slug,
        province: props.item.province.slug,
        dateIn: toRuralityDate().formatDate(state.dates.start as Date),
        dateOut: toRuralityDate().formatDate(state.dates.end as Date),
        capacity: state.customers?.toString(),
      }),
      { external: true },
    )

    isLoading.value = false
  }
}

onMounted(() => {
  if (!import.meta.client) return

  const initialSavedDates = getSavedDates()

  if (route.query.dateIn && route.query.dateOut) {
    state.dates = {
      start: toRuralityDate().parseDate(route.query.dateIn as string),
      end: toRuralityDate().parseDate(route.query.dateOut as string),
    }
    if (route.query.capacity) {
      state.customers = parseInt(route.query.capacity as string)
    }
    setCookieDates()
  } else if (initialSavedDates) {
    if (initialSavedDates.dateIn && initialSavedDates.dateOut) {
      state.dates = {
        start: toRuralityDate().parseDate(initialSavedDates.dateIn),
        end: toRuralityDate().parseDate(initialSavedDates.dateOut),
      }
    }
    if (initialSavedDates.capacity) {
      state.customers = parseInt(initialSavedDates.capacity)
    }
  }
})
</script>
